import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ShieldAlert, File, ClipboardList } from "lucide-react";
import FileDownloadButton from "@/components/FileDownloadButton";

export function RFQsList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/rfqs").then((r) => setItems(r.data.rfqs || [])); }, []);
  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">طلبات عروض الأسعار</div>
        <h1 className="text-3xl font-bold text-brand-navy">RFQs المستلمة</h1>
        <p className="text-slate-600 mt-1">جميع طلبات عروض الأسعار المُسندة لك من MERQAV. هوية العميل محمية.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="rfqs-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="px-4 py-3 text-start">رقم RFQ</th>
                <th className="px-4 py-3 text-start">القطعة</th>
                <th className="px-4 py-3 text-start">الكمية</th>
                <th className="px-4 py-3 text-start">التسليم المطلوب</th>
                <th className="px-4 py-3 text-start">الحالة</th>
                <th className="px-4 py-3 text-start"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`rfq-row-${r.rfq_number}`}>
                  <td className="px-4 py-3 font-semibold text-brand-navy">{r.rfq_number}</td>
                  <td className="px-4 py-3">{r.part_name}</td>
                  <td className="px-4 py-3">{r.quantity}</td>
                  <td className="px-4 py-3 text-slate-600">{r.delivery_date?.slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-end"><Link to={`/app/rfqs/${r.id}`} className="text-brand-amber font-semibold" data-testid={`view-rfq-${r.rfq_number}`}>عرض / تقديم عرض</Link></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">لا توجد طلبات عروض بعد.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

export function RFQDetail() {
  const { id } = useParams();
  const [rfq, setRfq] = useState(null);
  const [existing, setExisting] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [showAckDialog, setShowAckDialog] = useState(true);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState({
    manufacturing_cost: 0, material_cost: 0, tooling_cost: 0, setup_cost: 0,
    packaging_cost: 0, delivery_cost: 0, total_price: 0,
    lead_time_days: 14, validity_days: 30, warranty: "6 أشهر", moq: 1,
    technical_notes: "", assumptions: "", exclusions: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rfqs/${id}`);
      setRfq(data);
      const { data: mine } = await api.get("/workshop-quotations", { params: { request_id: data.request_id } });
      setExisting(mine.quotations?.[0] || null);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const recalc = () => {
    const total = ["manufacturing_cost", "material_cost", "tooling_cost", "setup_cost", "packaging_cost", "delivery_cost"]
      .reduce((s, k) => s + Number(q[k] || 0), 0);
    return total;
  };

  const submit = async () => {
    try {
      await api.post("/workshop-quotations", {
        rfq_id: id,
        ...Object.fromEntries(Object.entries(q).map(([k, v]) => [k, isNaN(Number(v)) || typeof v === "string" ? v : Number(v)])),
        total_price: recalc(),
      });
      toast.success("تم تقديم عرض السعر بنجاح");
      setShowQuoteDialog(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <DashboardLayout><Loader2 className="w-8 h-8 animate-spin text-brand-amber m-8" /></DashboardLayout>;
  if (!rfq) return null;

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">طلب عرض سعر</div>
        <h1 className="text-3xl font-bold text-brand-navy">{rfq.rfq_number}</h1>
        <p className="text-slate-600 mt-1"><ShieldAlert className="inline w-4 h-4 text-brand-amber me-1" /> {rfq.anonymous_customer}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="font-bold text-brand-navy mb-4">تفاصيل القطعة</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-500">القطعة:</span> {rfq.part_name}</div>
              <div><span className="text-slate-500">الكمية:</span> {rfq.quantity}</div>
              <div><span className="text-slate-500">المادة:</span> {rfq.material || "—"}</div>
              <div><span className="text-slate-500">الأبعاد:</span> {rfq.dimensions || "—"}</div>
              <div><span className="text-slate-500">التفاوت:</span> {rfq.tolerance || "—"}</div>
              <div><span className="text-slate-500">القطاع العام:</span> {rfq.industry_general}</div>
              <div><span className="text-slate-500">التسليم المطلوب:</span> {rfq.delivery_date?.slice(0, 10)}</div>
              <div><span className="text-slate-500">آخر موعد للعرض:</span> {rfq.deadline?.slice(0, 10)}</div>
              <div className="col-span-2"><span className="text-slate-500">وظيفة القطعة:</span> {rfq.part_function || "—"}</div>
              <div className="col-span-2"><span className="text-slate-500">وصف العطل:</span> {rfq.failure_description || "—"}</div>
              <div className="col-span-2"><span className="text-slate-500">ملاحظات MERQAV:</span> {rfq.notes || "—"}</div>
            </div>
          </div>

          {acknowledged && rfq.files?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-bold text-brand-navy mb-4">الملفات الفنية</h3>
              <div className="grid gap-2">
                {rfq.files.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 border border-slate-200 rounded-md p-3">
                    <File className="w-4 h-4 text-brand-amber" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{f.filename}</div>
                      <div className="text-xs text-slate-500">{f.ext?.toUpperCase()}</div>
                    </div>
                    <FileDownloadButton fileId={f.id} filename={f.filename} data-testid={`download-rfq-file-${f.id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          {existing ? (
            <div className="bg-white border-2 border-emerald-500 rounded-lg p-6" data-testid="existing-quotation">
              <h3 className="font-bold text-brand-navy mb-3">عرض السعر المقدم</h3>
              <div className="text-sm space-y-2">
                <div><span className="text-slate-500">الرقم:</span> {existing.quotation_number}</div>
                <div className="text-xl font-bold text-brand-navy">{existing.total_price?.toLocaleString()} ر.س</div>
                <div><span className="text-slate-500">مدة التسليم:</span> {existing.lead_time_days} يوم</div>
                <StatusBadge status={existing.status} />
              </div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <ClipboardList className="w-8 h-8 text-brand-amber mb-2" />
              <h3 className="font-bold text-brand-navy mb-2">تقديم عرض سعر</h3>
              <p className="text-sm text-slate-600 mb-4">قدم عرضك السري لهذا الطلب. سيراجعه فريق MERQAV فقط.</p>
              <Button className="btn-cta w-full" disabled={!acknowledged} onClick={() => setShowQuoteDialog(true)} data-testid="open-quote-form-btn">تقديم عرض السعر</Button>
              {!acknowledged && <div className="text-xs text-amber-700 mt-2">يجب الموافقة على تعهد السرية أولاً.</div>}
            </div>
          )}
        </div>
      </div>

      {/* NDA acknowledgement */}
      <Dialog open={!acknowledged && showAckDialog} onOpenChange={setShowAckDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>تعهد السرية</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-md p-4">
            أتعهد بالمحافظة على سرية جميع الرسومات والملفات والمعلومات الفنية، وعدم نسخها أو استخدامها
            أو مشاركتها خارج نطاق تنفيذ هذا الطلب.
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setShowAckDialog(false)}>إغلاق</Button>
            <Button className="btn-cta" onClick={() => { setAcknowledged(true); setShowAckDialog(false); }} data-testid="acknowledge-nda-btn">أوافق وأتعهد</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quote form dialog */}
      <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>نموذج عرض السعر (سري)</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["manufacturing_cost", "تكلفة التصنيع"],
              ["material_cost", "تكلفة المواد"],
              ["tooling_cost", "تكلفة العدد"],
              ["setup_cost", "تكلفة الإعداد"],
              ["packaging_cost", "تكلفة التغليف"],
              ["delivery_cost", "تكلفة التوصيل"],
              ["lead_time_days", "مدة التسليم (يوم)"],
              ["validity_days", "صلاحية العرض (يوم)"],
              ["moq", "الحد الأدنى للكمية"],
            ].map(([k, label]) => (
              <div key={k}><Label>{label}</Label><Input type="number" value={q[k]} onChange={(e) => setQ({ ...q, [k]: e.target.value })} className="mt-1.5" data-testid={`quote-${k}`} /></div>
            ))}
            <div className="col-span-2"><Label>الضمان</Label><Input value={q.warranty} onChange={(e) => setQ({ ...q, warranty: e.target.value })} className="mt-1.5" data-testid="quote-warranty" /></div>
            <div className="col-span-2"><Label>ملاحظات فنية</Label><Textarea value={q.technical_notes} onChange={(e) => setQ({ ...q, technical_notes: e.target.value })} className="mt-1.5" data-testid="quote-notes" /></div>
            <div><Label>افتراضات</Label><Textarea value={q.assumptions} onChange={(e) => setQ({ ...q, assumptions: e.target.value })} className="mt-1.5" data-testid="quote-assumptions" /></div>
            <div><Label>استثناءات</Label><Textarea value={q.exclusions} onChange={(e) => setQ({ ...q, exclusions: e.target.value })} className="mt-1.5" data-testid="quote-exclusions" /></div>
          </div>
          <div className="bg-brand-navy text-white rounded-md p-4 mt-3">
            <div className="text-2xl font-bold text-brand-amber">الإجمالي: {recalc().toLocaleString()} ر.س</div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setShowQuoteDialog(false)}>إلغاء</Button>
            <Button className="btn-cta" onClick={submit} data-testid="submit-workshop-quote-btn">تقديم العرض</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
